import { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth, GoogleLoginButton } from "./auth.js";
import { Gallery } from "./gallery.js";
import { RecordingProvider, RecordControl } from "./recording.js";
import { SessionsPanel } from "./sessions.js";
import { AccessPanel } from "./access.js";
import { navigate } from "./router.js";

export function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}

function Root() {
  const { state } = useAuth();

  if (state.status === "loading") {
    return (
      <main className="stage">
        <div className="badge badge--pending" data-testid="auth-loading">
          <span className="dot" /> Chargement…
        </div>
      </main>
    );
  }

  if (state.status === "anon") {
    return <LoginScreen error={state.error} />;
  }

  return <Shell />;
}

function LoginScreen({ error }: { error: string | null }) {
  return (
    <main className="stage" data-testid="login-screen">
      <div className="stage__inner">
        <h1 className="brand">Rafali Story</h1>
        <p className="tagline">L'histoire de la famille en photos</p>
        <div className="login">
          <GoogleLoginButton />
          {error && (
            <p className="login__error" data-testid="login-error">
              {error}
            </p>
          )}
        </div>
      </div>
      <footer className="stage__foot">Accès réservé à la famille</footer>
    </main>
  );
}

function Shell() {
  return (
    <RecordingProvider>
      <div className="app" data-testid="shell">
        <header className="topbar topbar--app">
          <span className="topbar__title">
            <button
              className="topbar__brand topbar__home"
              onClick={() => navigate({ kind: "home" })}
              title="Retour aux albums"
              data-testid="brand-home"
            >
              Rafali Story
            </button>
            <span className="topbar__version" data-testid="app-version">v{__APP_VERSION__}</span>
          </span>
          <div className="topbar__right">
            {/*
              L'enregistrement reste au propriétaire tant que la transcription n'est pas
              fiable : mesurée le 15/08/2026, elle produit quatre textes différents pour le
              même audio. Le filtrage réel est côté serveur (`visibility.ts`) — cacher ce
              bouton ne cacherait pas les souvenirs déjà produits.
            */}
            <OwnerOnly>
              <RecordControl />
            </OwnerOnly>
            <UserMenu />
          </div>
        </header>
        <Gallery />
      </div>
    </RecordingProvider>
  );
}

/** Avatar cliquable → menu (Sessions si propriétaire, Déconnexion). */
/** N'affiche ses enfants qu'au propriétaire. */
function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  return state.status === "authed" && state.isOwner ? <>{children}</> : null;
}

function UserMenu() {
  const { state, logout } = useAuth();
  const user = state.status === "authed" ? state.user : null;
  const isOwner = state.status === "authed" && state.isOwner;
  const [open, setOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  return (
    <div className="usermenu" ref={ref}>
      <button
        className="usermenu__avatar"
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu"
        data-testid="user-menu"
      >
        {user.picture ? (
          <img src={user.picture} alt="" />
        ) : (
          <span className="usermenu__initial">{user.name.charAt(0).toUpperCase()}</span>
        )}
      </button>
      {open && (
        <div className="usermenu__dropdown">
          {isOwner && (
            <button
              className="usermenu__item"
              data-testid="sessions-open"
              onClick={() => {
                setSessionsOpen(true);
                setOpen(false);
              }}
            >
              Sessions
            </button>
          )}
          {isOwner && (
            <button
              className="usermenu__item"
              data-testid="access-open"
              onClick={() => {
                setAccessOpen(true);
                setOpen(false);
              }}
            >
              Accès
            </button>
          )}
          <button className="usermenu__item" onClick={() => void logout()}>
            Déconnexion
          </button>
        </div>
      )}
      {sessionsOpen && <SessionsPanel onClose={() => setSessionsOpen(false)} />}
      {accessOpen && <AccessPanel onClose={() => setAccessOpen(false)} />}
    </div>
  );
}
